<script setup lang="ts">
import { useCmsTranslations } from "@shopware/composables";
import { getProductRoute } from "@shopware/helpers";
import { defu } from "defu";
import { computed, ref, unref } from "vue";
import type { ComputedRef } from "vue";
import { useRouter } from "vue-router";
import { useProductConfigurator } from "#imports";
import type { Schemas } from "#shopware";

const props = withDefaults(
  defineProps<{
    allowRedirect?: boolean;
  }>(),
  {
    allowRedirect: true,
  }
);

type Translations = {
  product: {
    chooseA: string;
  };
};

let translations: Translations = {
  product: {
    chooseA: "Choose a",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const emit =
  defineEmits<
    (e: "change", selected: Partial<Schemas["Product"]> | undefined) => void
  >();
const isLoading = ref<boolean>();
const router = useRouter();
const { product } = useProduct();
const {
  handleChange,
  getOptionGroups,
  getSelectedOptions,
  findVariantForSelectedOptions,
} = useProductConfigurator();
const { t } = useI18n();

const selectedOptions: ComputedRef = computed(() =>
  Object.values(unref(getSelectedOptions))
);
const isOptionSelected = (optionId: string) =>
  Object.values(getSelectedOptions.value).includes(optionId);

const onHandleChange = async () => {
  isLoading.value = true;
  const variantFound = await findVariantForSelectedOptions(
    unref(selectedOptions)
  );

  const selectedOptionsVariantPath = getProductRoute(variantFound);
  if (props.allowRedirect && selectedOptionsVariantPath) {
    try {
      router.push(selectedOptionsVariantPath);
    } catch {
      console.error("incorrect URL", selectedOptionsVariantPath);
    }
  } else {
    emit("change", variantFound);
  }
  isLoading.value = false;
};

const showCalculator = ref<boolean>(false);
const calculator = ref(null);

const quantityComparison = computed(
  () =>
    product.value?.translated?.customFields?.customfields_quantity_comparison
);

const height = ref(null);
const width = ref(null);
const coveragePerLiter = 13;

const totalArea = computed(() => {
  if (!height.value || !width.value) return null;
  return height.value * width.value;
});

const requiredLiters = computed(() => {
  if (!totalArea.value) return null;
  const liter = Math.ceil(totalArea.value / coveragePerLiter);
  return t("product.quantityCalculator.liter", { liter });
});
</script>

<template>
  <div class="flex flex-col">
    <div
      v-if="isLoading"
      class="absolute inset-0 flex items-center justify-center z-10 bg-white/75"
    >
      <div
        data-testid="loading"
        class="h-15 w-15 i-carbon-progress-bar-round animate-spin c-gray-500"
      />
    </div>
    <div v-for="optionGroup in getOptionGroups" :key="optionGroup.id">
      <h3 class="text-sm text-gray-900 font-medium tracking-wide mb-0">
        {{ optionGroup.name }}
      </h3>
      <fieldset class="mt-4 flex-1">
        <legend class="sr-only">
          {{ optionGroup.name }}
        </legend>
        <div class="mb-8 xl:mb-12">
          <div class="mb-4">
            <div class="flex gap-2 text-base md:text-xl md:gap-4">
              <label
                v-for="option in optionGroup.options"
                :key="option.id"
                class="relative cursor-pointer"
                @click="
                  handleChange(optionGroup.name, option.id, onHandleChange)
                "
              >
                <span
                  :id="`${option.id}-choice-label`"
                  class="block px-3 md:px-4 py-[5px] md:py-2 border rounded-lg text-gray-700 border-gray-300"
                  :class="{
                    'bg-green-500 text-white border-green-500 bg-cust_green':
                      isOptionSelected(option.id),
                    'flex items-center gap-2': !option.media,
                    'flex flex-col items-center gap-1': option.media,
                  }"
                >
                  <NuxtImg
                    v-if="option.media"
                    :src="option.media.url"
                    :alt="option.media.translated.alt || ''"
                    class="w-12 h-12 object-cover"
                  />
                  <span
                    v-else-if="
                      optionGroup.name.toLowerCase().includes('farbton')
                    "
                    class="w-4 h-4 rounded-full border border-gray-300"
                    :style="{ backgroundColor: option.name }"
                  />
                  {{ option.name }}
                </span>
              </label>
            </div>
          </div>

          <div>
            <p class="text-sm md:text-lg">
              {{ quantityComparison }}
              <span class="text-cust_green inline-block mx-1">
                <i class="fa-solid fa-arrow-right"></i>
              </span>
              <button @click="showCalculator = true" class="loadQty underline">
                {{ $t("product.quantityCalculator.label") }}
              </button>
              <span class="text-cust_green inline-block mx-1">
                <i class="fa-regular fa-copy"></i>
              </span>
            </p>
          </div>

          <div
            ref="calculator"
            class="popup-wrapper popup02 w-screen h-screen bg-[rgba(126,160,129,0.4)] fixed top-0 start-0 z-[51] flex items-center justify-center"
            :class="{ show: showCalculator }"
          >
            <div
              class="py-7 md:py-12 px-4 md:px-8 rounded-2xl md:rounded-3xl bg-white md:max-w-[480px] max-w-[94%] flex flex-col gap-4 md:gap-6"
            >
              <div class="flex gap-5 items-center justify-between">
                <h4 class="mb-0">
                  {{ $t("product.quantityCalculator.title") }}
                </h4>
                <button
                  @click="showCalculator = false"
                  class="closePop text-2xl md:text-3xl text-cust_gray cursor-pointer"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
              <p>
                {{ $t("product.quantityCalculator.description") }}
              </p>
              <div class="flex justify-between items-center">
                <div>
                  <input
                    type="number"
                    class="inputField"
                    min="1"
                    v-model.number="height"
                    :placeholder="$t('product.quantityCalculator.height')"
                  />
                </div>
                <div class="text-cust_gray text-lg md:text-2xl">
                  <i class="fa-solid fa-xmark"></i>
                </div>
                <div>
                  <input
                    type="number"
                    class="inputField"
                    min="1"
                    v-model.number="width"
                    :placeholder="$t('product.quantityCalculator.width')"
                  />
                </div>
                <div class="text-cust_gray text-lg md:text-2xl">
                  <i class="fa-solid fa-arrow-right"></i>
                </div>
                <div>
                  <input
                    type="number"
                    class="inputField w-[113px] md:w-[145px]"
                    readonly
                    :value="totalArea?.toFixed(2)"
                    :placeholder="$t('product.quantityCalculator.totalArea')"
                  />
                </div>
              </div>
              <div class="flex items-center justify-between text-sm md:text-lg">
                <p>
                  {{ $t("product.quantityCalculator.projectRequirementLabel") }}
                </p>
                <div>
                  <input
                    type="text"
                    class="inputField w-[113px] md:w-[145px]"
                    :value="requiredLiters"
                    readonly
                    :placeholder="
                      $t('product.quantityCalculator.totalRequiredPlaceholder')
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  </div>
</template>
